import { Howl, Howler } from "howler";

export type SoundId =
  | "tap"
  | "correct"
  | "wrong"
  | "launch"
  | "liftoff"
  | "unlock";

const SOUND_FILES: Record<SoundId, string> = {
  tap: "/sounds/tap.wav",
  correct: "/sounds/correct.wav",
  wrong: "/sounds/wrong.wav",
  launch: "/sounds/launch.wav",
  liftoff: "/sounds/liftoff.wav",
  unlock: "/sounds/unlock.wav",
};

const MUTE_KEY = "rocket-builder-muted";

let unlocked = false;
let muted = false;
const howls = new Map<SoundId, Howl>();

function isMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod|Android|Mobile/i.test(navigator.userAgent);
}

function loadMuted(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
}

function ensureHowl(id: SoundId): Howl {
  let howl = howls.get(id);
  if (!howl) {
    howl = new Howl({
      src: [SOUND_FILES[id]],
      volume: id === "liftoff" ? 0.55 : 0.7,
      preload: true,
      // HTML5 audio is more reliable on iOS/Android than Web Audio for short SFX.
      html5: isMobileDevice(),
    });
    howls.set(id, howl);
  }
  return howl;
}

function preloadAll() {
  for (const id of Object.keys(SOUND_FILES) as SoundId[]) {
    ensureHowl(id);
  }
}

function resumeAudioContext() {
  const ctx = Howler.ctx;
  if (ctx?.state === "suspended") {
    void ctx.resume();
  }
}

/** Call on user gesture — preloads sounds and resumes the audio context. */
export function unlockAudio(): void {
  if (typeof window === "undefined") return;
  if (!unlocked) {
    unlocked = true;
    muted = loadMuted();
    preloadAll();
  }
  Howler.mute(muted);
  resumeAudioContext();
}

export function isAudioUnlocked(): boolean {
  return unlocked;
}

export function getInitialMuted(): boolean {
  return loadMuted();
}

export function isMuted(): boolean {
  return muted;
}

export function setMuted(next: boolean): void {
  muted = next;
  if (typeof window !== "undefined") {
    localStorage.setItem(MUTE_KEY, next ? "1" : "0");
  }
  Howler.mute(next);
}

export function playSound(id: SoundId): void {
  if (typeof window === "undefined") return;

  // Game sounds always follow a tap/drag; unlock lazily so we don't depend on
  // a separate window listener firing before Pixi canvas touches on mobile.
  unlockAudio();
  if (muted) return;

  resumeAudioContext();

  try {
    ensureHowl(id).play();
  } catch {
    // Ignore playback errors (e.g. missing file during dev).
  }
}
